import { Component, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { pick } from 'lodash-es';
import { ScrBaseTabComponent } from 'src/app/components/base.component';
import { Lease } from 'src/app/models/lease.model';
import { Address } from 'src/app/services/address-book.service';
import { ConfigService } from 'src/app/services/config.service';
import { ShortCodesService } from 'src/app/services/shortcodes.service';
import { handleEnterKeyPress } from 'src/app/shared/common-events';
import { CampaignType, OrderTransactionType, PartnerType, PoliticalCampaignStatus, VetStatus } from 'src/app/models/constants';
import { ConfirmationService, SelectItem, SelectItemGroup } from 'primeng/api';
import { PartnerService } from 'src/app/services/partner.service';
import { MultiSelect, MultiSelectChangeEvent } from 'primeng/multiselect';
import { Message } from '../../../../shared/messages/messages.component';
import { Partner } from '../../../../models/partner.model';

@Component({
  templateUrl: './edit-details.component.html',
  styleUrls: ['./edit-details.component.scss']
})
export class EditDetailsComponent extends ScrBaseTabComponent implements OnInit {

  public scId: number;
  public isInvoiceUpdate = false;
  public model!: Lease;
  public aggregators = [];
  public loaded = false;
  public campaignTypeChoice?: CampaignType;
  public displayDialog = false;
  public codeIsParked = false;
  maxSelectedLabels = 5;
  public codeIsDemo = false;
  public codeType = '';
  public selectedCdeType = '';
  public publicEmailDomains: string[] = [];

  public OrderTransactionType = OrderTransactionType;

  constructor(
    private route: ActivatedRoute,
    private configService: ConfigService,
    private shortCodesService: ShortCodesService,
    protected confirmationService: ConfirmationService,
    private brandService: PartnerService
  ) {
    super();

    this.tt = 'Edit Application';
    this.routeElement = 'details';

    this.scId = this.route.snapshot.params.scId;
  }

  public brandOptions!: SelectItem<number>[] | SelectItemGroup[];
  public validBrandIds!: number[];
  public contentProviderOptions!: SelectItem<number>[];
  public validContentProviderIds!: number[];

  partnersValidated = false;
  brandError?: Message;
  usagesEnabled: string[] = ['active', 'demo', 'parked'];

  campaignTypeOptions = [
    { label: 'Federal', value: 'Federal' },
    { label: 'Local', value: 'Local' },
    { label: 'Other', value: 'Other' },
    { label: 'State', value: 'State' },
    { label: 'Tribal', value: 'Tribal' }
  ];

  ngOnInit(): void {
    this.configService.getConfig().subscribe(cfg => {
      this.publicEmailDomains = cfg.publicEmailDomains;
    });
    this.subscriptions.push(
      this.route.params.subscribe({
        next: (params) => {
          this.log.trace('edit details params are now ' + JSON.stringify(params));
          this.scId = params.scId;
          this.fetch();
        }
      }));
  }

  fetch(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.forkedObserve<any>([
      this.configService.getAggregators(),
      this.shortCodesService.getApplication(this.scId),
      this.initBrandOptions(),
      this.configService.getConfig()
    ], res => {
      this.aggregators = res[0];
      this.model = res[1].leasedCodeDetail;

      if (this.model.parkedCode) {
        this.usagesEnabled = ['active', 'demo', 'parked'];
      }
      else if (this.model.demoCode) {
        this.usagesEnabled = ['active', 'demo'];
      }
      else {
        this.usagesEnabled = ['active'];
      }
      // filter out brands that are not successful.
      if (!this.model.brands) {
        this.model.brands = [];
      }
      let brands = this.model.brands;
      brands = brands.filter(b => {
        return b.vetStatus === VetStatus.Successful;
      });

      this.model.brandIds = brands.map(b => b.partnerId);

      if (brands.length !== this.model.brands.length) {
        const badOnes = this.model.brands.filter(b => b.vetStatus !== VetStatus.Successful);
        // display missing warning.
        const names = badOnes.map(b => b.company).join(', ');
        const m = new Message();
        m.title = 'Warning';
        m.dismissible = true;
        m.text = `If you save your updates, the Brand Client(s): ${names} cannot be included because their vet status is no longer Successful.`;
        this.brandError = m;
      }

      this.model.brands = [];

      if (!this.model.forPoliticalCampaign) {
        this.model.fecId = '';
        this.model.politicalCampaignType = undefined;
        this.model.politicalCampaignInfo = '';
      }
      else {
        if (!this.model.politicalCampaignType) {
          this.campaignTypeChoice = CampaignType.Federal;
          this.model.politicalCampaignType = CampaignType.Federal;
          this.model.politicalCampaignInfo = '';
        } else {
          this.campaignTypeChoice = this.model.politicalCampaignType;
          if (this.model.politicalCampaignType === 'Federal')
            this.model.politicalCampaignInfo = '';
          else {
            this.model.fecId = '';
          }
        }
      }

      if (this.model.demoCode) {
        this.codeType = 'demo';
      } else if (this.model.parkedCode) {
        this.codeType = 'parked';
      } else {
        this.codeType = 'active';
      }
      this.selectedCdeType = this.codeType;

      this.loaded = true;
      return false;
    });
  }

  override getObjectId(): string {
    return `${this.scId}`;
  }

  getRegionRequired(): boolean {
    return this.model.country == 'us' || this.model.country == 'ca';
  }

  next({ form }: NgForm): void {
    this.clearMessages();
    document.getElementById('application-form')?.classList.remove('presubmit');

    form.markAllAsTouched();
    for (const name in form.controls) {
      form.controls[name].markAsDirty();
    }

    if (form.invalid) {
      const firstError = document.querySelector('.ng-invalid .ng-invalid')?.parentElement;
      if (firstError) { firstError.scrollIntoView(); }
    }
    else {
      this.observe(this.shortCodesService.patchApplication(this.scId, this.model, this.isInvoiceUpdate), () => {
        window.setTimeout(() => this.cancel(), 0);
        return false;
      });
    }
  }

  cancel() {
    document.querySelector('a.active .btn')?.dispatchEvent(new Event('click'));
  }

  loadApplicantAddress(address: Address) {
    Object.assign(this.model,
      pick(address,
        ['firstName', 'lastName', 'phone', 'phoneExtension', 'email', 'address1', 'address2', 'city', 'state', 'zip', 'country']));
  }

  handleEnter(event: Event) {
    handleEnterKeyPress(event);
  }


  selectCampaignType() {
    this.model.forPoliticalCampaign = true;
    if (this.model.politicalCampaignType) {
      this.campaignTypeChoice = this.model.politicalCampaignType;
    }
    else {
      this.campaignTypeChoice = CampaignType.Federal;
      this.model.politicalCampaignType = CampaignType.Federal;
    }

    if (this.model.politicalCampaignType === CampaignType.Federal)
      this.model.politicalCampaignInfo = undefined;//'';
    else
      this.model.fecId = undefined;//'';


  }

  onChangeCampaignType(campaignTypeChoice?: CampaignType) {
    this.model.politicalCampaignType = campaignTypeChoice;
    this.model.politicalCampaignStatus = PoliticalCampaignStatus.Pending;

    if (this.model.politicalCampaignType === CampaignType.Federal)
      this.model.politicalCampaignInfo = undefined; //'';
    else
      this.model.fecId = undefined;//'';
  }

  onModelChange() {
    this.model.politicalCampaignStatus = PoliticalCampaignStatus.Pending;
  }

  clearFields() {
    this.model.fecId = '';
    this.campaignTypeChoice = undefined;
    this.model.politicalCampaignInfo = '';
    this.model.politicalCampaignType = undefined;
    this.model.politicalCampaignComments = undefined;
  }

  initBrandOptions(): Promise<Partner[]> {

    return new Promise((res, rej) => {
      this.observe(this.brandService.retrievePartners(VetStatus.Successful), {
        onSuccess: (rsp => {
          if (rsp.partners) {
            const brands = rsp.partners.filter(p => p.partnerType === PartnerType.Brand);
            const cps = rsp.partners.filter(p => p.partnerType === PartnerType.ContentProvider);
            this.validBrandIds = brands.map(p => p.partnerId);
            this.brandOptions = brands.map(b => { return { 'label': b.company, 'value': b.partnerId }; });
            this.validContentProviderIds = cps.map(p => p.partnerId);
            this.contentProviderOptions = cps.map(b => { return { 'label': b.company, 'value': b.partnerId }; });
            res(rsp.partners);
          }
          else {
            this.validBrandIds = [];
            this.validContentProviderIds = [];
            res([]);
          }
          return false;
        }),
        onError: (e => {
          rej(e);
        }),
        onFailure: (rsp => {
          rej(rsp);
        })
      });
    });
  }
  /**
   * This is a workaround for a primeng bug where the max selected labels does not
   * work when the display is in chip mode.   Return chip mode normally but comma
   * mode when the "<n> Brands Selected" is to be displayed.
   */
  get multiSelectDisplay() {
    return (this.model?.brandIds?.length ?? 0) > this.maxSelectedLabels ? 'comma' : 'chip';
  }


  setCodeType(codeType: string): void {
    if (this.codeType !== codeType) {
      this.isInvoiceUpdate = true;
    }
    this.codeType = codeType;
    if (codeType === 'active') {
      this.model.demoCode = false;
      this.model.parkedCode = false;
      this.codeIsDemo = false;
      this.codeIsParked = false;
    }
    if (codeType === 'demo') {
      this.model.demoCode = true;
      this.model.parkedCode = false;
      this.model.brandIds = [];
      this.codeIsDemo = true;
      this.codeIsParked = false;
    }
    if (codeType === 'parked') {
      this.model.demoCode = false;
      this.model.parkedCode = true;
      this.model.brandIds = [];
      this.codeIsDemo = false;
      this.codeIsParked = true;
    }
  }

  /**
   * A brand was selected.  Don't let the total selection exceed 50.
   * @param e - selection event
   */
  brandSelected(m: MultiSelect, e: MultiSelectChangeEvent) {
    // e.value is an array of brandIds.
    // e.itemValue is the brandId and label.
    if (e.value.length > 50) {
      // reject it.
      let sel: number[] = e.value ?? [];
      sel = sel.filter(v => v !== e.itemValue?.value);
      m.updateModel(sel);
    }
  }
}
